import StageQr from "@/components/StageQr";
import { useState } from "react";
import EquipmentOptions from "@/components/EquipmentOptions";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import WindingStrip from "@/components/WindingStrip";

// The Slitting stage as its two paper forms (PF005/F02-00): page 07 "5. Slitting & Numbering"
// (line clearance, the slitting details, winding direction) and page 08 "6. Slitting & Numbering"
// (numbering, joints, the production and joints tables). Shown as two tabs, printed as two sheets.
// The stage's quantities (which the waste alert and the Packing stage read) are the tables' totals.
const SLIT_MIN = 5, ROLL_MIN = 6, JOINT_MIN = 5;
const SLIT_KEYS = ["date", "start", "finish", "issued", "wastage", "operator"];
const blankSlit = () => ({ date: "", start: "", finish: "", issued: "", wastage: "", operator: "" });
const blankRoll = () => ({ labels: "", rolls: "" });
const blankJoint = () => ({ reel: "", joints: "", qty: "" });
const has = (v) => v !== undefined && v !== null && v !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => (Math.round(n * 1000) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const arr = (v) => (Array.isArray(v) ? v : []);

// Fields of this stage the sheets cover; the rest stay available under "More details".
export const SLIT_SHEET_FIELDS = new Set([
  "date", "start_time", "end_time", "operator_name", "qty_received_from_avt", "qty_numbering", "qty_forward_to_packing",
  "qty_wastage", "rolls_breakdown", "total_qty", "total_no_of_rolls", "remarks", "equipment_id", "previous_job_name",
  "previous_product_code", "lc_done_by", "lc_checked_by", "slit_rows", "done_by_production", "checked_by_qc", "winding_ticks",
  "labels_per_roll", "numbering_from", "numbering_to", "joint_type", "tapes_used", "no_numbering_joint", "roll_rows",
  "joint_rows", "total_joints", "joints_total_qty",
]);

/** 5.10 rows: as saved, or, for records made before the table existed, one row from the single figures. */
export function slitRows(data) {
  if (arr(data.slit_rows).length) return data.slit_rows;
  const single = ["qty_received_from_avt", "qty_wastage", "date", "start_time", "end_time"];
  if (!single.some((k) => has(data[k]))) return [];
  return [{ date: data.date ?? "", start: data.start_time ?? "", finish: data.end_time ?? "",
    issued: data.qty_received_from_avt ?? "", wastage: data.qty_wastage ?? "", operator: data.operator_name ?? "" }];
}

export function slitFigures(data) {
  const rows = slitRows(data);
  const issued = rows.reduce((s, r) => s + num(r.issued), 0);
  const wastage = rows.reduce((s, r) => s + num(r.wastage), 0);
  const rolls = arr(data.roll_rows);
  const joints = arr(data.joint_rows);
  const from = parseFloat(data.numbering_from), to = parseFloat(data.numbering_to);
  return {
    rows, issued, wastage, good: issued - wastage,
    rollCount: rolls.reduce((s, r) => s + num(r.rolls), 0),
    rollTotal: rolls.reduce((s, r) => s + num(r.labels) * num(r.rolls), 0),
    jointCount: joints.reduce((s, r) => s + num(r.joints), 0),
    jointQty: joints.reduce((s, r) => s + num(r.qty), 0),
    numbered: Number.isFinite(from) && Number.isFinite(to) && to >= from ? to - from + 1 : null,
  };
}

/** The stage fields to save for the current tables: the quantities other stages read. */
export function slitTotalsPatch(data) {
  const f = slitFigures(data);
  const patch = {};
  if (f.rows.length) {
    const first = f.rows[0];
    const finished = [...f.rows].reverse().find((r) => has(r.finish));
    Object.assign(patch, {
      qty_received_from_avt: f.issued, qty_wastage: f.wastage, qty_forward_to_packing: f.good,
      ...(has(first.date) && { date: first.date }), ...(has(first.start) && { start_time: first.start }),
      ...(finished && { end_time: finished.finish }),
      // Page 07's first row names the operator; page 08's "Operator Name" is the same person —
      // carried across live so it's typed once, same as the date and start time above.
      ...(has(first.operator) && { operator_name: first.operator }),
    });
  }
  const rolls = arr(data.roll_rows).filter((r) => has(r.labels) || has(r.rolls));
  if (rolls.length) {
    Object.assign(patch, { total_qty: f.rollTotal, total_no_of_rolls: f.rollCount,
      rolls_breakdown: rolls.map((r) => `${r.labels || 0} x ${r.rolls || 0}`).join("\n") });
  }
  if (arr(data.joint_rows).some((r) => has(r.joints) || has(r.qty))) Object.assign(patch, { total_joints: f.jointCount, joints_total_qty: f.jointQty });
  if (f.numbered !== null) patch.qty_numbering = f.numbered;
  return patch;
}

const line = "border border-neutral-700";
const cell = `${line} px-1.5 py-1 text-[11px] align-middle`;
const input = "w-full bg-transparent outline-none text-[11px] font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";
const dotted = "border-b border-dotted border-neutral-600 pb-0.5";

/** `data` is the stage record, `ctx` its context (the job, what AVT carried, the job card's winding), `setMany(patch)` changes keys. */
export default function SlittingSheet({ ctx, data, setMany, readOnly }) {
  const [page, setPage] = useState(1);
  const common = ctx?.common || {};
  const carried = ctx?.carried || {};
  const f = slitFigures(data);
  const ticks = data.winding_ticks && typeof data.winding_ticks === "object" ? data.winding_ticks : ctx?.winding || {};
  const change = (patch) => setMany({ ...patch, ...slitTotalsPatch({ ...data, ...patch }) });

  const slits = [...f.rows, ...Array.from({ length: Math.max(SLIT_MIN - f.rows.length, 0) }, blankSlit)];
  const rolls = [...arr(data.roll_rows), ...Array.from({ length: Math.max(ROLL_MIN - arr(data.roll_rows).length, 0) }, blankRoll)];
  const joints = [...arr(data.joint_rows), ...Array.from({ length: Math.max(JOINT_MIN - arr(data.joint_rows).length, 0) }, blankJoint)];
  const editList = (key, list, keys, minKept) => (i, k, v) => {
    const next = list.map((r) => ({ ...r }));
    next[i][k] = v;
    change({ [key]: next.filter((r, at) => at < minKept || keys.some((x) => has(r[x]))) });
  };
  const editSlit = editList("slit_rows", slits, SLIT_KEYS, f.rows.length);
  const editRoll = editList("roll_rows", rolls, ["labels", "rolls"], arr(data.roll_rows).length);
  const editJoint = editList("joint_rows", joints, ["reel", "joints", "qty"], arr(data.joint_rows).length);

  // Render helpers, not components: a component defined here would remount on every keystroke.
  const text = (label, k, { value = data[k] ?? "", width = "", type = "text" } = {}) => (
    <div key={k} className={`flex items-end gap-1.5 ${width}`}>
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <input type={type} list={k === "equipment_id" ? "equipment-ids" : undefined} className={`${input} ${dotted}`} value={value} disabled={readOnly}
        onChange={(e) => (k === "numbering_from" || k === "numbering_to" ? change({ [k]: e.target.value }) : setMany({ [k]: e.target.value }))}
        data-testid={`slit-${k}`} />
    </div>
  );
  const letterhead = (title, pageNo) => (
    <div className="flex items-start justify-between px-3 pt-2 pb-1">
      <div>
        <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
        <div className="print-only text-[10px] text-neutral-700">
          BPR {common.mpwo_number} · {[common.customer_name, common.product_name].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="font-semibold text-[12px] border-b border-neutral-700 px-6">{title}</div>
      <div className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage="slitting" /><div className="text-[11px] text-neutral-600">Page {pageNo} of 11</div></div>
    </div>
  );
  const signs = (a, b) => (
    <div className="grid grid-cols-2 gap-x-6">
      {text("Done by & Date (Production)", a)}
      {text("Checked by & Date (Q.C.)", b)}
    </div>
  );
  const th = "text-left font-semibold leading-tight";

  return (
    <div className="space-y-2" data-testid="slitting-sheet">
      <EquipmentOptions types={["Slitter", "Rewinder"]} />
      <div className="no-print flex flex-wrap items-center gap-1 border border-neutral-200 bg-white p-1 rounded-sm w-fit" data-testid="slitting-tabs">
        {[[1, "Page 07 of 11 · Line clearance, slitting details, winding"], [2, "Page 08 of 11 · Numbering, production, joints"]].map(([n, label]) => (
          <button key={n} type="button" onClick={() => setPage(n)} data-testid={`slitting-tab-${n}`}
            className={`px-3 py-1.5 text-xs rounded-sm font-medium transition-colors ${page === n ? "bg-[#FF5A00] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ---- page 07 ---- */}
      <div className={`bg-white border border-neutral-700 text-neutral-900 ${page === 1 ? "" : "hidden"}`} data-testid="slitting-page-1">
        {letterhead("5. SLITTING & NUMBERING", "07")}
        <div className="px-3 pb-2 space-y-1.5">
          <div className={`relative ${line} rounded-sm pt-3 p-2 mt-2.5`}>
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[12px] font-bold underline whitespace-nowrap">Line Clearance</div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
              {text("Equipment I.D.:", "equipment_id")}
              <span />
              <span />
              {text("Previous Job Name:", "previous_job_name")}
              <div className="flex items-end gap-1.5">
                <span className="text-[11px] whitespace-nowrap">W/o No.:</span>
                <span className={`${dotted} flex-1 text-[11px] font-semibold font-mono-num`}>{common.mpwo_number}</span>
              </div>
              {text("Previous Product Code:", "previous_product_code")}
            </div>
          </div>
          <div className={`${line} rounded-sm`}>
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className={`${cell} w-10 text-center font-semibold`}>5.1</td>
                  <td className={cell}>Clean the Slitting &amp; Numbering area before starting the job.</td>
                </tr>
                <tr>
                  <td className={`${cell} text-center font-semibold`}>5.2</td>
                  <td className={cell}>
                    Check the working area before going to slitting. Clear the previous product before start up &amp; Check the same size &amp; same colour jobs are
                    not taken for consecutive Production. Throw the wastage into dustbin and check the area and assure that the area is free of previous job material.
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="px-1.5 pb-1.5">{signs("lc_done_by", "lc_checked_by")}</div>
          </div>

          <div className="text-[11px] flex gap-3 pt-1"><span className="font-semibold">5.10</span><span>Mention the details of Slitting operation in following table.</span></div>
          <table className="w-full border-collapse" data-testid="slit-rows">
            <thead>
              <tr className="align-top">
                {["Date", "Start Time", "Finish Time", "Issued Qty. from AVT (Labels)", "Wastage Qty. (Labels)", "Total Good Qty. (Labels)", "Operator Sign"].map((h) => (
                  <th key={h} className={`${cell} ${th}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slits.map((r, i) => {
                const filled = has(r.issued) || has(r.wastage);
                return (
                  <tr key={i} className="h-7">
                    <td className={cell}><input type="date" className={input} value={r.date ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "date", e.target.value)} data-testid={`slit-row-${i}-date`} /></td>
                    <td className={cell}><input type="time" className={input} value={r.start ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "start", e.target.value)} data-testid={`slit-row-${i}-start`} /></td>
                    <td className={cell}><input type="time" className={input} value={r.finish ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "finish", e.target.value)} data-testid={`slit-row-${i}-finish`} /></td>
                    <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.issued ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "issued", e.target.value)} data-testid={`slit-row-${i}-issued`} /></td>
                    <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.wastage ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "wastage", e.target.value)} data-testid={`slit-row-${i}-wastage`} /></td>
                    <td className={`${cell} text-right font-mono-num`} data-testid={`slit-row-${i}-good`}>{filled ? fmt(num(r.issued) - num(r.wastage)) : ""}</td>
                    <td className={cell}><input className={input} value={r.operator ?? ""} disabled={readOnly} onChange={(e) => editSlit(i, "operator", e.target.value)} data-testid={`slit-row-${i}-operator`} /></td>
                  </tr>
                );
              })}
              {f.rows.length > 1 && (
                <tr className="bg-neutral-50 font-semibold">
                  <td className={cell} colSpan={3}>Total</td>
                  <td className={`${cell} text-right font-mono-num`}>{fmt(f.issued)}</td>
                  <td className={`${cell} text-right font-mono-num`}>{fmt(f.wastage)}</td>
                  <td className={`${cell} text-right font-mono-num`} data-testid="slit-good-total">{fmt(f.good)}</td>
                  <td className={cell} />
                </tr>
              )}
            </tbody>
          </table>
          {!readOnly && (
            <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
              onClick={() => change({ slit_rows: [...slits, blankSlit()] })} data-testid="slit-add-row">
              <Plus size={11} /> Add row
            </Button>
          )}

          <div className={`${line} p-1.5`} data-testid="slit-winding">
            <div className="flex gap-3 text-[11px]"><span className="font-semibold">5.11</span><span>Mention the Winding Direction as per the Customer Specification :</span></div>
            <WindingStrip ticks={ticks} onChange={(next) => setMany({ winding_ticks: next })} readOnly={readOnly} prefix="slit" />
          </div>
          <div className={`${line} p-1.5 space-y-2`}>
            <div className="flex gap-3 text-[11px]"><span className="font-semibold">5.12</span><span>After completion of job place them in trays and send them for packing.</span></div>
            {signs("done_by_production", "checked_by_qc")}
          </div>
          <div className="text-[11px]">Wastage quantity shall be entered in reconciliation column on page 10.</div>
        </div>
        <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
      </div>

      {/* ---- page 08 ---- */}
      <div className={`bg-white border border-neutral-700 text-neutral-900 ${page === 2 ? "" : "hidden"}`} data-testid="slitting-page-2">
        {letterhead("6. SLITTING & NUMBERING", "08")}
        <div className="px-3 pb-2 space-y-2">
          <div className="space-y-1.5 pt-1">
            {text("Operator Name", "operator_name", { width: "max-w-md" })}
            {text("Number of Labels per roll", "labels_per_roll", { value: data.labels_per_roll ?? common.labels_per_roll ?? "", width: "max-w-md" })}
            <div className="grid grid-cols-2 gap-x-6 max-w-2xl">
              {text("Numbering from", "numbering_from")}
              {text("To", "numbering_to")}
            </div>
            <div className="grid grid-cols-2 gap-x-6 max-w-2xl">
              {text("Joint Type", "joint_type")}
              {text("Tapes used for joint", "tapes_used")}
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <span>No numbering before and after joint :</span>
              {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => (
                <label key={v} className="inline-flex items-center gap-1.5">
                  <input type="checkbox" className="accent-[#FF5A00] h-3.5 w-3.5" checked={data.no_numbering_joint === v} disabled={readOnly}
                    onChange={(e) => setMany({ no_numbering_joint: e.target.checked ? v : "" })} data-testid={`slit-nonum-${v}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="font-bold text-[12px] underline pt-1">PRODUCTION</div>
          <div className="text-[11px]">Fill the following table :</div>
          <table className="w-full border-collapse" data-testid="slit-rolls">
            <thead>
              <tr>{["Labels", "Rolls", "Total"].map((h) => <th key={h} className={`${cell} ${h === "Total" ? "text-center" : "text-center"} font-semibold`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rolls.map((r, i) => (
                <tr key={i} className="h-7">
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.labels ?? ""} disabled={readOnly} onChange={(e) => editRoll(i, "labels", e.target.value)} data-testid={`slit-roll-${i}-labels`} /></td>
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.rolls ?? ""} disabled={readOnly} onChange={(e) => editRoll(i, "rolls", e.target.value)} data-testid={`slit-roll-${i}-rolls`} /></td>
                  <td className={`${cell} text-right font-mono-num`} data-testid={`slit-roll-${i}-total`}>{has(r.labels) && has(r.rolls) ? fmt(num(r.labels) * num(r.rolls)) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!readOnly && (
            <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
              onClick={() => change({ roll_rows: [...rolls, blankRoll()] })} data-testid="slit-add-roll">
              <Plus size={11} /> Add row
            </Button>
          )}
          <div className="text-[11px] text-right pr-1">
            Grand Total : <b className="font-mono-num" data-testid="slit-grand-total">{f.rollTotal ? fmt(f.rollTotal) : ""}</b>
            {f.rollCount > 0 && <span className="text-neutral-600"> &nbsp;({fmt(f.rollCount)} rolls)</span>}
          </div>

          <div className="text-[11px] pt-1">Mention the no. of joints in the following table :</div>
          <table className="w-full border-collapse" data-testid="slit-joints">
            <thead>
              <tr>{["Reel Number", "Number of Joints", "Quantity per reel"].map((h) => <th key={h} className={`${cell} text-center font-semibold`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {joints.map((r, i) => (
                <tr key={i} className="h-7">
                  <td className={cell}><input className={input} value={r.reel ?? ""} disabled={readOnly} onChange={(e) => editJoint(i, "reel", e.target.value)} data-testid={`slit-joint-${i}-reel`} /></td>
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.joints ?? ""} disabled={readOnly} onChange={(e) => editJoint(i, "joints", e.target.value)} data-testid={`slit-joint-${i}-joints`} /></td>
                  <td className={cell}><input type="number" className={`${input} text-right font-mono-num`} value={r.qty ?? ""} disabled={readOnly} onChange={(e) => editJoint(i, "qty", e.target.value)} data-testid={`slit-joint-${i}-qty`} /></td>
                </tr>
              ))}
              <tr className="bg-neutral-50 font-semibold">
                <td className={`${cell} text-right`}>Grand Total :</td>
                <td className={`${cell} text-right font-mono-num`} data-testid="slit-joints-total">{f.jointCount ? fmt(f.jointCount) : ""}</td>
                <td className={`${cell} text-right font-mono-num`} data-testid="slit-joints-qty-total">{f.jointQty ? fmt(f.jointQty) : ""}</td>
              </tr>
            </tbody>
          </table>
          {!readOnly && (
            <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
              onClick={() => change({ joint_rows: [...joints, blankJoint()] })} data-testid="slit-add-joint">
              <Plus size={11} /> Add row
            </Button>
          )}
          <div>
            <div className="text-[11px] font-bold">Remarks :</div>
            <textarea className={`${input} ${dotted} min-h-9 resize-none`} value={data.remarks ?? ""} disabled={readOnly}
              onChange={(e) => setMany({ remarks: e.target.value })} data-testid="slit-remarks" />
          </div>
        </div>
        <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
      </div>
    </div>
  );
}
